import React, { useState } from 'react';

import StatusBanner from '../../components/status-banner/StatusBanner.jsx';
import { PrimaryButton, SecondaryButton } from '../../components/button/Button.jsx';
import { retryServerNow } from './appStatus.js';
import useAppStatus from './useAppStatus.js';

import './AppStatusBar.css';

/**
 * The one place the app says it is not in its normal state, floating over the foot of every page (App.jsx). Each
 * message says what happened and what the user can do; none blocks the page. Shown in order of what matters most:
 *
 *   offline            "You're offline."                    - clears itself when the connection is back
 *   serverUnreachable  "Can't reach GeoPlay right now."     - retries on its own; Retry Now
 *   newVersion         "A new version of GeoPlay is available." - Reload Now, or Later (updates on next page change)
 *
 * An expired session is the sign-in dialog (SessionExpiredDialog), not a line here.
 */
const AppStatusBar = () => {
    const { offline, serverUnreachable, newVersion } = useAppStatus();
    const [dismissedVersion, setDismissedVersion] = useState(null);

    let banner = null;
    if (offline) {
        banner = (
            <StatusBanner
                status="warning"
                header="You're offline."
                subheader="We'll reconnect automatically when your connection is back. Anything you haven't saved stays on this page."
            />
        );
    } else if (serverUnreachable) {
        banner = (
            <StatusBanner
                status="warning"
                busy
                header="Can't reach GeoPlay right now."
                subheader="Retrying automatically. Anything you haven't saved stays on this page."
                action={<SecondaryButton size="small" onClick={retryServerNow}>Retry Now</SecondaryButton>}
            />
        );
    } else if (newVersion && newVersion !== dismissedVersion) {
        banner = (
            <StatusBanner
                status="info"
                header="A new version of GeoPlay is available."
                subheader="Reload to update, or it updates when you next change page."
                action={<PrimaryButton size="small" onClick={() => window.location.reload()}>Reload Now</PrimaryButton>}
                onClose={() => setDismissedVersion(newVersion)}
            />
        );
    }

    if (!banner) return null;
    return <div className="app-status-bar">{banner}</div>;
};

export default AppStatusBar;
